import React from 'react';
import { cn } from './utils';

interface FormFieldProps {
  label?: string;
  error?: string;
  helpText?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
  htmlFor?: string;
}

/**
 * FormField - Clean, Calendly-inspired form field wrapper
 * 
 * Features:
 * - Consistent label and error styling
 * - Help text support
 * - Required field indicators
 * - Proper spacing and accessibility
 * - Consistent with design system
 */
const FormField: React.FC<FormFieldProps> = ({
  label,
  error,
  helpText,
  required = false,
  className,
  children,
  htmlFor,
}) => {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className={cn(
            'block text-sm font-medium text-gray-700',
            required && 'after:content-["*"] after:text-red-500 after:ml-1'
          )}
        >
          {label}
        </label>
      )}
      
      {children}
      
      {error && (
        <p className="text-sm text-red-600 flex items-start">
          <svg 
            className="h-4 w-4 mt-0.5 mr-1 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
              clipRule="evenodd" 
            />
          </svg>
          {error}
        </p>
      )}
      
      {helpText && !error && (
        <p className="text-sm text-gray-500">{helpText}</p>
      )}
    </div>
  );
};

export default FormField;